import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { hrApi } from '../api';
import type { InviteEmployeeInput } from '../types';
import { workspaceApi } from '../../workspace/api';
import type { WorkspaceRole } from '../../workspace/types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onInvited: () => void;
}

// agent_required defaults FALSE: a new hire is a regular (web clock-in)
// employee; monitoring is an explicit, per-seat-billed toggle — here or on the
// employee's detail page — never a side effect of inviting someone.
const EMPTY: InviteEmployeeInput = {
  email: '', name: '', employee_no: '', department: '', designation: '', hire_date: null,
  scheduled_clock_in: null, scheduled_clock_out: null, hourly_rate: null,
  monthly_base_salary: null,
  agent_required: false,
};

export function InviteEmployeeDialog({ open, onOpenChange, onInvited }: Props) {
  const [form, setForm] = React.useState<InviteEmployeeInput>(EMPTY);
  const [busy, setBusy] = React.useState(false);

  // Roles are optional: only inviters with access.manage can read them.
  // If the fetch 403s (or otherwise fails) we hide the selector and invite
  // as before (the backend assigns the default employee role).
  const [roles, setRoles] = React.useState<WorkspaceRole[] | null>(null);
  const [roleKey, setRoleKey] = React.useState<string>('');

  React.useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setRoles(null);
    setRoleKey('');
    workspaceApi.listRoles()
      .then((r) => {
        // Require an explicit choice — do NOT silently pre-select a role, so the
        // inviter consciously decides what this person can access.
        setRoles(r);
      })
      .catch(() => setRoles(null)); // 403 or error → selector stays hidden
  }, [open]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.email?.trim() || !form.name?.trim()) {
      toast.error('Email and name are required');
      return;
    }
    setBusy(true);
    try {
      await hrApi.inviteEmployee({
        email: form.email.trim(),
        name: form.name.trim(),
        employee_no: form.employee_no?.trim() || null,
        department: form.department?.trim() || null,
        designation: form.designation?.trim() || null,
        hire_date: form.hire_date || null,
        scheduled_clock_in: form.scheduled_clock_in || null,
        scheduled_clock_out: form.scheduled_clock_out || null,
        hourly_rate: form.hourly_rate ? Number(form.hourly_rate) : null,
        monthly_base_salary: form.monthly_base_salary ? Number(form.monthly_base_salary) : null,
        agent_required: form.agent_required ?? false,
        // Only send role_keys when the selector was shown and a role chosen.
        ...(roles && roleKey ? { role_keys: [roleKey] } : {}),
      });
      toast.success('Invitation sent', { description: `Magic link emailed to ${form.email}` });
      onInvited();
    } catch (err: any) {
      toast.error('Invite failed', { description: err.message });
    } finally { setBusy(false); }
  }

  // Payroll's buildBaseComponent prefers monthly_base_salary and only falls back
  // to hourly_rate when it is null — setting both is legal but makes the hourly
  // rate dead weight, so warn rather than block.
  const bothPaySet = !!form.monthly_base_salary && !!form.hourly_rate;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Invite an employee</DialogTitle>
          <DialogDescription>
            They'll receive a magic-link email to set their password and log in.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Email<span className="text-red-500 dark:text-red-400 ml-0.5">*</span></Label>
              <Input type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div className="grid gap-1.5">
              <Label>Name<span className="text-red-500 dark:text-red-400 ml-0.5">*</span></Label>
              <Input value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="grid gap-1.5">
              <Label>Employee #</Label>
              <Input value={form.employee_no ?? ''}
                onChange={(e) => setForm({ ...form, employee_no: e.target.value })} placeholder="EMP-0001" />
            </div>
            <div className="grid gap-1.5">
              <Label>Department</Label>
              <Input value={form.department ?? ''}
                onChange={(e) => setForm({ ...form, department: e.target.value })} />
            </div>
            <div className="grid gap-1.5">
              <Label>Designation</Label>
              <Input value={form.designation ?? ''}
                onChange={(e) => setForm({ ...form, designation: e.target.value })} />
            </div>
            <div className="grid gap-1.5">
              <Label>Hire date</Label>
              <Input type="date" value={form.hire_date ?? ''}
                onChange={(e) => setForm({ ...form, hire_date: e.target.value || null })} />
            </div>
            <div className="grid gap-1.5">
              <Label>Clock-in (HH:MM)</Label>
              <Input type="time" value={form.scheduled_clock_in ?? ''}
                onChange={(e) => setForm({ ...form, scheduled_clock_in: e.target.value || null })} />
            </div>
            <div className="grid gap-1.5">
              <Label>Clock-out (HH:MM)</Label>
              <Input type="time" value={form.scheduled_clock_out ?? ''}
                onChange={(e) => setForm({ ...form, scheduled_clock_out: e.target.value || null })} />
            </div>
            <div className="grid gap-1.5">
              <Label>Monthly salary</Label>
              <Input type="number" min={0} step="0.01" value={form.monthly_base_salary ?? ''}
                onChange={(e) => setForm({ ...form, monthly_base_salary: e.target.value ? Number(e.target.value) : null })} />
              <p className="text-[11.5px] text-muted-foreground">Salaried. Leave blank for hourly.</p>
            </div>
            <div className="grid gap-1.5">
              <Label>Hourly rate</Label>
              <Input type="number" min={0} step="0.01" value={form.hourly_rate ?? ''}
                onChange={(e) => setForm({ ...form, hourly_rate: e.target.value ? Number(e.target.value) : null })} />
              <p className="text-[11.5px] text-muted-foreground">Paid per attendance hour.</p>
            </div>
            <div className="col-span-2">
              <p className="text-[11.5px] text-muted-foreground">
                Set <strong>one</strong> of the two pay fields.{' '}
                {bothPaySet ? (
                  <span className="text-amber-700 dark:text-amber-400 font-semibold">
                    Both are set — payroll will pay the monthly salary and ignore the hourly rate.
                  </span>
                ) : (
                  'A monthly salary is paid flat each run (prorated for a partial month); an hourly rate is paid on recorded hours.'
                )}
              </p>
            </div>
            <div className="grid gap-1.5 col-span-2">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.agent_required === true}
                  onChange={(e) => setForm({ ...form, agent_required: e.target.checked })}
                  className="mt-1 h-4 w-4 rounded border-border accent-saffron"
                />
                <span>
                  <span className="text-[13px] font-medium text-foreground block">
                    Monitored employee (desktop app required)
                  </span>
                  <span className="text-[12px] text-muted-foreground block mt-0.5">
                    They'll get the app install &amp; setup card on their dashboard automatically, and
                    browser clock-in is disabled for them. Billed at the monitoring seat rate.
                    Leave off for a regular web clock-in employee.
                  </span>
                </span>
              </label>
            </div>
            {roles && roles.length > 0 && (
              <div className="grid gap-1.5 col-span-2">
                <Label>Role<span className="text-red-500 dark:text-red-400 ml-0.5">*</span></Label>
                <Select value={roleKey} onValueChange={setRoleKey}>
                  <SelectTrigger><SelectValue placeholder="Select a role" /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.id} value={r.key}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11.5px] text-muted-foreground">
                  Controls what this person can access. Manage roles under Roles &amp; access (Settings → Access).
                </p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy || (!!roles && !roleKey)}>{busy ? 'Sending…' : 'Send invite'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
